/**
 * Microsoft Clarity (session replays and heatmaps) for the web and desktop app, through Microsoft's own package
 * (`@microsoft/clarity`, which injects the official https://www.clarity.ms/tag/<id> script).
 *
 * - Project: config/clarity.json `web` ("Bundle Web App", shared by web and desktop). Empty = never loads.
 * - Consent: driven by AnalyticsService (same regional rule as GA4, core logic/analyticsConsent). Nothing loads before
 *   analytics is collecting; then `consentv2` grants analytics storage only (ads storage denied). Withdrawing sends
 *   `consentv2` denied, stops the recording and expires Clarity's cookies (_clck, _clsk).
 * - Strict masking, enforced in code: `data-clarity-mask="True"` on <html> masks every text node, input and image in
 *   the app before the script loads, whatever the dashboard says (Clarity: the attribute overrides the project's
 *   masking mode). Nothing in the app ever sets `data-clarity-unmask`. The dashboard is set to Strict as well.
 * - Identity: only the opaque telemetry id (core logic/telemetry), never a name or an email.
 */
import Clarity from "@microsoft/clarity";
import clarityConfig from "../../../../config/clarity.json";

const PROJECT_ID: string = (import.meta.env.VITE_CLARITY_PROJECT_ID as string | undefined) || clarityConfig.web;

type ClarityQueue = ((...args: unknown[]) => void) | undefined;

class ClarityReplays {
  private started = false;
  private collecting = false;
  private userId: string | undefined;

  /** Masks everything before anything could record (idempotent). */
  private maskAll(): void {
    document.documentElement.setAttribute("data-clarity-mask", "True");
  }

  /** Called by AnalyticsService whenever the consent outcome changes (`on` only in production builds). */
  setCollecting(on: boolean, userId: string | undefined): void {
    if (userId !== undefined) this.userId = userId;
    if (PROJECT_ID === "" || typeof document === "undefined") return;
    if (on === this.collecting) return;
    this.collecting = on;
    try {
      if (on) {
        this.maskAll();
        if (!this.started) {
          this.started = true;
          Clarity.init(PROJECT_ID);
        } else {
          queue()?.("start");
        }
        Clarity.consentV2({ ad_Storage: "denied", analytics_Storage: "granted" });
        if (this.userId !== undefined) Clarity.identify(this.userId);
      } else if (this.started) {
        Clarity.consentV2({ ad_Storage: "denied", analytics_Storage: "denied" });
        queue()?.("stop");
        clearClarityCookies();
      }
    } catch {
      // Blocked by an extension: replays are best effort.
    }
  }

  /** The opaque telemetry id of the signed-in user (undefined: signed out). */
  identify(userId: string | undefined): void {
    this.userId = userId;
    if (!this.collecting || userId === undefined) return;
    try {
      Clarity.identify(userId);
    } catch {
      // best effort
    }
  }

  /** A product event name (no parameters), to filter replays. */
  event(name: string): void {
    if (!this.collecting) return;
    try {
      Clarity.event(name);
    } catch {
      // best effort
    }
  }
}

function queue(): ClarityQueue {
  return (window as unknown as { clarity?: ClarityQueue }).clarity;
}

function clearClarityCookies(): void {
  const parts = location.hostname.split(".");
  const domains = ["", ...parts.slice(0, -1).map((_, i) => `; domain=.${parts.slice(i).join(".")}`)];
  for (const name of ["_clck", "_clsk"]) for (const d of domains) document.cookie = `${name}=; Max-Age=0; path=/${d}`;
}

export const clarity = new ClarityReplays();
